import request from "./request";
import wx from "weixin-js-sdk";

// 存储初始化状态（避免重复调用config）
let isWxConfigInited = false;

/**
 * 初始化微信JSSDK配置
 * @param {Array} extraJsApiList - 额外需要的JS接口列表（默认包含分享和图片接口，可补充其他接口）
 * @returns {Promise<boolean>} - 初始化成功/失败
 */
export async function initWxConfig(extraJsApiList = []) {
  // 1. 避免重复初始化
  if (isWxConfigInited) {
    console.log("JSSDK已初始化，无需重复调用");
    return true;
  }

  try {
    // 2. 获取当前页面URL（微信要求去掉#及后面部分，否则签名失败）
    const currentUrl = window.location.href.split("#")[0];
    // 3. 调用后端接口获取签名配置
    const response = await request.get(
      `/wxindex/wxservice/jssign.html?url=${encodeURIComponent(currentUrl)}`
    );

    // 4. 合并基础接口和额外接口（确保包含公众号跳转所需的 reJumpToAppPage）
    const baseJsApiList = [
      "updateTimelineShareData", // 分享到朋友圈
      "updateAppMessageShareData", // 分享给朋友
      "chooseImage", // 选择图片
      "previewImage", // 预览图片
      "uploadImage", // 上传图片
      "downloadImage", // 下载图片
      "getLocalImgData", // 获取本地图片数据
      "showAllNonBaseMenuItem", // 显示所有非基础菜单
      "chooseWXPay",
      "hideMenuItems"
    ];
    // 去重合并接口列表
    const finalJsApiList = [...new Set([...baseJsApiList, ...extraJsApiList])];

    // 5. 调用wx.config初始化
    wx.config({
      debug: false, // 开发时可设为true，通过弹窗查看错误信息
      appId: response.appId, // 后端返回的公众号AppID
      timestamp: response.timestamp, // 后端返回的时间戳（秒级）
      nonceStr: response.nonceStr, // 后端返回的随机字符串
      signature: response.signature, // 后端返回的签名
      jsApiList: finalJsApiList, // 最终的接口列表
      openTagList: ["wx-open-launch-weapp"]
    });

    // 6. 监听初始化结果，更新状态
    return new Promise((resolve, reject) => {
      // 初始化成功
      wx.ready(() => {
        isWxConfigInited = true;
        console.log("JSSDK初始化成功，支持的接口：", finalJsApiList);
        resolve(true);
      });

      // 初始化失败（如签名错误、域名未配置等）
      wx.error((err) => {
        console.error("JSSDK初始化失败：", err);
        // 常见错误提示：帮助排查问题
        const errorTips = {
          signature: "签名错误（检查URL、appSecret、jsapi_ticket是否正确）",
          urldomain: "域名未配置（检查JS接口安全域名是否添加当前域名）",
          appid: "AppID错误（检查后端返回的appId是否正确）"
        };
        console.log(`微信环境初始化失败：${errorTips[err.errMsg] || err.errMsg}`);
        reject(false);
      });
    });

  } catch (error) {
    console.error("获取JSSDK签名配置失败：", error);
    console.log("微信环境配置加载失败，请刷新页面重试");
    return false;
  }
}

/**
 * 设置微信分享配置（支持分享到朋友圈/朋友）
 * @param {Object} options - 分享参数
 * @param {string} options.title - 分享标题（默认：新民晚报读者俱乐部）
 * @param {string} options.desc - 分享描述（默认：新民晚报社读者资讯、活动互动）
 * @param {string} options.link - 分享链接（默认：https://clubs.xinmin.cn/dzclub/index.html）
 * @param {string} options.imgUrl - 分享图标（默认：https://clubs.xinmin.cn/dzclub/share.png）
 */
export function setWxShare({
  title = "新民晚报读者俱乐部",
  desc = "新民晚报社读者资讯、活动互动",
  link = "https://clubs.xinmin.cn/dzclub/index.html",
  imgUrl = "https://clubs.xinmin.cn/dzclub/share.png",
} = {}) {
  // 1. 先判断wx是否存在且已初始化（避免未初始化时调用）
  // if (!isWxConfigInited) {
  //   console.warn("wx未加载或未初始化，暂不设置分享");
  //   return;
  // }

  // 2. 页面路径判断：单页应用用 hash 判断更精准（避免URL参数干扰）
  const currentHash = window.location.hash; // 拿 # 后的路由（如 #/productdetail?xxx）
  const noHidePageFlag = "/productdetail";
  const isNoHidePage = currentHash.includes(noHidePageFlag);

  if (!isNoHidePage) {
    console.log("非商品页，显示分享按钮并配置分享");
    // 先显示所有非基础菜单（确保分享按钮没被隐藏），再配置分享
    wx.showAllNonBaseMenuItem({
      success: () => {
        // 配置朋友圈分享
        wx.updateTimelineShareData({ title, link, imgUrl });
        // 配置朋友分享
        wx.updateAppMessageShareData({ title, desc, link, imgUrl });
        console.log("商品页分享配置完成");
      },
      fail: (err) => {
        console.error("显示菜单失败（商品页）：", err);
      },
    });
    return;
  }

  // 其他页面：隐藏分享和复制按钮
  wx.hideMenuItems({
    menuList: [
      "menuItem:share:appMessage",
      "menuItem:share:timeline",
      "menuItem:copyUrl",
    ],
    success: () => {
      console.log("商品页：隐藏分享和复制按钮成功");
    },
    fail: (err) => {
      console.error("隐藏菜单失败（非商品页）：", err);
    },
  });
}
